/* eslint-disable no-template-curly-in-string */
/* eslint-disable max-len */
import React, { useCallback, useEffect, useState, Fragment } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Formik, FieldArray } from "formik";
import uniqueId from "lodash/uniqueId";
import { InputAdornment, Grid } from "@mui/material";
import {
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  InputGroup,
  LinkedField,
  CustomSelect,
  DatePicker,
  Checkbox,
  TimePicker,
  InlineRadioGroup
} from "../../../../../components/Form";
import { Divider } from "../../../../../components/Divider";
import { LinkButton } from "../../../../../components/Buttons/LinkButton";
import { YES_NO_NA_OPTIONS } from "../../../../../constants";
import { DirhamSymbol } from "../../../../../components/DirhamSymbol";
import { cloneDeep } from "../../../../../utils/cloneDeep";
import { MapModal, MapPreview } from "../../../../../components/Modals/GoogleMap";
import { getRoDocuments } from "../../../../../store/selectors/appConfig";
import { getIsEditableStatusSearchInfo } from "../../../../../store/selectors/searchProspect";
import { isAgendDocumentUploaded } from "../../../../../utils/documents";
import { documentUnique } from "../../../../../constants/documents";
import { RoDocument } from "../../../../UploadDocuments/components/RoDocument/RoDocument";
import { SubmitButton } from "../../../../../components/Buttons/SubmitButton";
import { Button, DeleteButton, PreviewContainer, MapPreviewContainer, FormStyle } from "../styled";
import { ScrollToError } from "components/ScrollToError";

import {
  initialVisitValidations,
  initialValues,
  changeDateProspectHandler,
  changeTimeProspectHandler,
  companyDetailsSchema,
  CustomerBackgroundInvalidReporter
} from "./utils";
import {
  COMPANY_BACKGROUND,
  RO_ADDITIONAL_COMMENTS,
  RM_ADDITIONAL_COMMENTS,
  SO_ADDITIONAL_COMMENTS,
  HOD_ADDITIONAL_COMMENTS,
  MAX_COMPANY_BACKGROUND_LENGTH,
  OTHER_CONTACT_DESIGNATION
} from "./constants";

export const CompanyDetails = ({
  handleContinue,
  createFormChangeHandler,
  otherCompanySignboardProof,
  outsourcedStaffProof,
  telephoneOrDEWAProof,
  organizationInfoRO,
  signboardProof,
  updateVisit,
  isExistingCustomer,
  onFieldInvalidChange = () => {},
  invalidFields = {}
}) => {
  const [showMap, setShowMap] = useState(false);
  const isComeFromROScreens = useSelector(getIsEditableStatusSearchInfo);
  const [isOutsourcedStaffProofUploaded, setisOutsourcedStaffProofUploaded] = useState(false);
  const roDocuments = useSelector(getRoDocuments);
  const roProspectPath = "roProspect.organizationInfoRO";

  useEffect(() => {
    if (
      isAgendDocumentUploaded(roDocuments.companyDocuments, documentUnique.ProofOfOutsourceStaff)
    ) {
      setisOutsourcedStaffProofUploaded(true);
    } else {
      setisOutsourcedStaffProofUploaded(false);
    }
  }, [roDocuments]);

  const handleShowMap = useCallback(() => {
    setShowMap(true);
  }, []);

  const handleCloseMap = useCallback((coords, setFieldValue) => {
    setFieldValue("longitude", coords.lng);
    setFieldValue("latitude", coords.lat);
    setShowMap(false);
  }, []);

  const addVisitHandler = (values, setFieldValue) => {
    let currentVisitValidations = cloneDeep(values.visitValidations);
    currentVisitValidations.push({ ...cloneDeep(initialVisitValidations), id: uniqueId() });
    setFieldValue("visitValidations", currentVisitValidations);
    const arr = currentVisitValidations.map(({ id, ...keepAttrs }) => keepAttrs);
    updateVisit(arr);
  };

  const handleDeleteVisit = (entryIndex, values, setFieldValue) => {
    const newValues = values.visitValidations.filter((_, index) => entryIndex !== index);
    const newArray = cloneDeep(newValues);
    setFieldValue(
      "visitValidations",
      newValues.length ? newValues : [{ ...initialValues, id: uniqueId() }]
    );
    const arr = newArray.map(({ id, ...keepAttrs }) => keepAttrs);
    updateVisit(arr);
  };
  // Utility functions moved to utils.js

  return (
    <Formik
      // enableReinitialize={true}
      initialValues={{
        visitValidations: organizationInfoRO.visit.map(item => ({
          ...item,
          counterfeitProducts:
            item.counterfeitProducts && item.counterfeitProducts !== ""
              ? item.counterfeitProducts
              : "No",
          id: uniqueId()
        })),
        isDedupeCase: false,
        isExistingCustomerNochange: false,
        premisesDetails: organizationInfoRO.premisesDetails,
        additionalDetails: organizationInfoRO.additionalDetails
      }}
      validationSchema={companyDetailsSchema}
      validateOnChange={false}
      onSubmit={handleContinue}
    >
      {createFormChangeHandler(({ setFieldValue, values }) => (
        <FormStyle>
          <ScrollToError />
          <MapModal
            openModal={showMap}
            handleClose={coords => handleCloseMap(coords, setFieldValue)}
            lat={values.premisesDetails.latitude}
            lng={values.premisesDetails.longitude}
          />
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <Field
                name={"isDedupeCase"}
                path={`${roProspectPath}.isDedupeCase`}
                label="Dedupe Case"
                component={Checkbox}
                inputProps={{ tabIndex: 0 }}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Field
                name={"isExistingCustomerNochange"}
                path={`${roProspectPath}.isExistingCustomerNochange`}
                label="Existing Customer - No change"
                component={Checkbox}
                inputProps={{ tabIndex: 0 }}
              />
            </Grid>
          </Grid>

          <FieldArray
            name="visitValidations"
            render={arrayHelper =>
              values.visitValidations.map((item, visitIndex) => {
                const visitDltBtn = values.visitValidations.length > 1;
                return (
                  <Fragment key={item.id}>
                    <Grid container columnSpacing={3} rowSpacing={3}>
                      <Grid item sm={visitDltBtn ? 8 : 12} xs={visitDltBtn ? 6 : 12}>
                        <h5 className="mb-0"> Visit </h5>
                      </Grid>
                      {visitDltBtn && (
                        <Grid item sm={4} xs={6} alignContent={"center"} mt="auto">
                          <DeleteButton>
                            <LinkButton
                              clickHandler={() =>
                                handleDeleteVisit(visitIndex, values, setFieldValue)
                              }
                              title="Delete"
                            />
                          </DeleteButton>
                        </Grid>
                      )}
                      <Grid item sm={6} xs={12}>
                        <InputGroup>
                          <Field
                            name={`visitValidations[${visitIndex}].contactSalutation`}
                            path={`${roProspectPath}.visit[${visitIndex}].contactSalutation`}
                            datalistId="salutation"
                            component={CustomSelect}
                            shrink={false}
                            inputProps={{ tabIndex: 0 }}
                            ErrorMessageComponent={() => null}
                          />
                          <Field
                            name={`visitValidations[${visitIndex}].contactfullName`}
                            path={`${roProspectPath}.visit[${visitIndex}].contactfullName`}
                            linkedFieldName={`visitValidations[${visitIndex}].contactSalutation`}
                            label="Contact's full name"
                            placeholder="Contact's full name"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                            ErrorMessageComponent={() => null}
                          />
                        </InputGroup>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <InputGroup>
                          <Field
                            name={`visitValidations[${visitIndex}].countryCode`}
                            linkedFieldName={`visitValidations[${visitIndex}].mobileNo`}
                            path={`${roProspectPath}.visit[${visitIndex}].countryCode`}
                            linkedPath={`${roProspectPath}.visit[${visitIndex}].mobileNo`}
                            datalistId="countryCode"
                            component={CustomSelect}
                            isSearchable
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />

                          <LinkedField
                            name={`visitValidations[${visitIndex}].mobileNo`}
                            path={`${roProspectPath}.visit[${visitIndex}].mobileNo`}
                            linkedFieldName={`visitValidations[${visitIndex}].countryCode`}
                            linkedPath={`${roProspectPath}.visit[${visitIndex}].countryCode`}
                            label="Contact's mobile"
                            placeholder="Contact's mobile"
                            component={Input}
                            contextualHelpText="This number should be unique for a Company"
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </InputGroup>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].contactDesignation`}
                          path={`${roProspectPath}.visit[${visitIndex}].contactDesignation`}
                          label="Contact's designation"
                          placeholder="Contact's designation"
                          component={SelectAutocomplete}
                          datalistId="contactDesignationSA"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      {values.visitValidations[visitIndex].contactDesignation ===
                        OTHER_CONTACT_DESIGNATION && (
                        <Grid item sm={6} xs={12}>
                          <Field
                            name={`visitValidations[${visitIndex}].contactDesignationOther`}
                            path={`${roProspectPath}.visit[${visitIndex}].contactDesignationOther`}
                            label="Please Specify"
                            placeholder="Please Specify"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </Grid>
                      )}
                      <Grid item sm={9} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].roName`}
                          path={`${roProspectPath}.visit[${visitIndex}].roName`}
                          label="Name of RO"
                          placeholder="Name of RO"
                          component={SelectAutocomplete}
                          datalistId="roName"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].roDateOfVisit`}
                          label="Date of visit"
                          path={`${roProspectPath}.visit[${visitIndex}].roDateOfVisit`}
                          component={DatePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeDateProspectHandler}
                          maxDate={new Date()}
                        />
                      </Grid>

                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].roTimeOfVisit`}
                          path={`${roProspectPath}.visit[${visitIndex}].roTimeOfVisit`}
                          label="Time of visit"
                          component={TimePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeTimeProspectHandler}
                        />
                      </Grid>
                      <Grid item sm={9} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].rmName`}
                          path={`${roProspectPath}.visit[${visitIndex}].rmName`}
                          label="Name of RM"
                          component={SelectAutocomplete}
                          datalistId="rmName"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid item sm={9} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].conductedByAgency`}
                          path={`${roProspectPath}.visit[${visitIndex}].conductedByAgency`}
                          label="Conducted by agency"
                          component={SelectAutocomplete}
                          datalistId="conductedByAgency"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid item sm={9} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].companyRepMet`}
                          path={`${roProspectPath}.visit[${visitIndex}].companyRepMet`}
                          label="Company representative met"
                          component={SelectAutocomplete}
                          datalistId="YesNoList"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      {/* <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].rmDateOfVisit`}
                          label="Date of visit"
                          path={`${roProspectPath}.visit[${visitIndex}].rmDateOfVisit`}
                          component={DatePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>

                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].rmTimeOfVisit`}
                          path={`${roProspectPath}.visit[${visitIndex}].rmTimeOfVisit`}
                          label="Time of visit"
                          component={TimePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid> */}
                    </Grid>
                    <Grid container spacing={3} mt={1}>
                      {/* <Grid item sm={9} xs={12}>
                        <InputGroup>
                          <Field
                            name={`visitValidations[${visitIndex}].rmCountryCode`}
                            path={`${roProspectPath}.visit[${visitIndex}].rmCountryCode`}
                            datalistId="countryCode"
                            component={CustomSelect}
                            shrink={false}
                            inputProps={{ tabIndex: 0 }}
                          />
                          <LinkedField
                            name={`visitValidations[${visitIndex}].rmMobileNo`}
                            path={`${roProspectPath}.visit[${visitIndex}].rmMobileNo`}
                            linkedFieldName={`visitValidations[${visitIndex}].rmCountryCode`}
                            linkedPath={`${roProspectPath}.visit[${visitIndex}].rmCountryCode`}
                            label="Representative's mobile no"
                            placeholder="Representative's mobile no"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </InputGroup>
                      </Grid> */}
                      <Grid item sm={9} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].addressOfPlaceMet`}
                          path={`${roProspectPath}.visit[${visitIndex}].addressOfPlaceMet`}
                          label="Address of place met"
                          component={SelectAutocomplete}
                          datalistId="placeMet"
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p> Did you notice counterfeit products at the time of visit? </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].counterfeitProducts`}
                          path={`${roProspectPath}.visit[${visitIndex}].counterfeitProducts`}
                          label=""
                          component={InlineRadioGroup}
                          datalistId="YesNoList"
                          typeRadio
                          contextualHelpProps={{ isDisableHoverListener: false }}
                          isLoadDefaultValueFromStore={false}
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p> Was the signatory of the company met during this visit? </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].signatoryMetDuringVisit`}
                          path={`${roProspectPath}.visit[${visitIndex}].signatoryMetDuringVisit`}
                          component={SelectAutocomplete}
                          label="Please select"
                          datalistId="YesNoList"
                          typeRadio
                          contextualHelpProps={{ isDisableHoverListener: false }}
                        />
                      </Grid>
                      {values.visitValidations[`${visitIndex}`].signatoryMetDuringVisit ===
                        "No" && (
                        <Grid item sm={9} xs={12}>
                          <Field
                            name={`visitValidations[${visitIndex}].reasonForNotmeetingSignatory`}
                            path={`${roProspectPath}.visit[${visitIndex}].reasonForNotmeetingSignatory`}
                            label="If no, for what reason"
                            placeholder="If no, for what reason"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </Grid>
                      )}
                      {values.visitValidations[`${visitIndex}`].signatoryMetDuringVisit ===
                        "Yes" && (
                        <Grid item sm={9} xs={12}>
                          <Field
                            name={`visitValidations[${visitIndex}].eidValidationDone`}
                            path={`${roProspectPath}.visit[${visitIndex}].eidValidationDone`}
                            label="If yes, was EID validation conducted?"
                            placeholder="If yes, was EID validation conducted?"
                            component={SelectAutocomplete}
                            datalistId="YesNoList"
                          />
                        </Grid>
                      )}
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p> Relationship of the signatory between partners/POA holder </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].signatoryRelationship.relationship`}
                          path={`${roProspectPath}.visit[${visitIndex}].signatoryRelationship.relationship`}
                          label="Please select"
                          component={SelectAutocomplete}
                          datalistId="contactRelationshipSA"
                          onChange={value => {
                            setFieldValue(
                              `visitValidations[${visitIndex}].signatoryRelationship.relationship`,
                              value
                            );
                          }}
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>

                      {values.visitValidations[`${visitIndex}`].signatoryRelationship
                        .relationship === "Rel6" && (
                        <Grid item sm={12} xs={12}>
                          <Field
                            name={`visitValidations[${visitIndex}].signatoryRelationship.others`}
                            path={`${roProspectPath}.visit[${visitIndex}].signatoryRelationship.others`}
                            label="Other(Specify)"
                            placeholder="Other(Specify)"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </Grid>
                      )}
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p>
                          Is there any change in company profile from previous borrowing relation
                          with RAK?
                        </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].isChangeInCompnayProfile`}
                          path={`${roProspectPath}.visit[${visitIndex}].isChangeInCompnayProfile`}
                          label="Please select"
                          component={SelectAutocomplete}
                          datalistId="flagWithNa"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>

                      {values.visitValidations[`${visitIndex}`].isChangeInCompnayProfile ===
                        "Yes" && (
                        <Grid item sm={12} xs={12}>
                          <Field
                            name={`visitValidations[${visitIndex}].changeInCompnayProfileRemarks`}
                            path={`${roProspectPath}.visit[${visitIndex}].changeInCompnayProfileRemarks`}
                            label="Change in company profile remarks"
                            placeholder="Change in company profile remarks"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </Grid>
                      )}
                      {isExistingCustomer === true && (
                        <>
                          <Grid container item alignItems="center" sm={6} xs={12}>
                            <p> Profile change updated in the system </p>
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <Field
                              name={`visitValidations[${visitIndex}].isprofileChangeUpdatedInSystem`}
                              path={`${roProspectPath}.visit[${visitIndex}].isprofileChangeUpdatedInSystem`}
                              label="Please select"
                              component={SelectAutocomplete}
                              datalistId="flagWithNa"
                              tabIndex="0"
                              isSearchable
                            />
                          </Grid>
                        </>
                      )}
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p> Estimated company asset and value </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].estimatedComapnyAsset`}
                          path={`${roProspectPath}.visit[${visitIndex}].estimatedComapnyAsset`}
                          label="Please select"
                          component={SelectAutocomplete}
                          datalistId="companyAssetEstimateSA"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].assetrdescription`}
                          path={`${roProspectPath}.visit[${visitIndex}].assetrdescription`}
                          label="Description/ breakdown of asset and value"
                          placeholder="Description/ breakdown of asset and value"
                          component={Input}
                          multiline
                          rows="4"
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p> RTA list of vehicles provided? </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].isRTAVehicleListProvided`}
                          path={`${roProspectPath}.visit[${visitIndex}].isRTAVehicleListProvided`}
                          label="Please select"
                          component={SelectAutocomplete}
                          datalistId="flagWithNa"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={6} xs={12}>
                        <p>
                          Is the company into activity which is prohibited as per Islamic Law for
                          Amal finance
                        </p>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].isCompanyInIslamicProhibitedActivity`}
                          path={`${roProspectPath}.visit[${visitIndex}].isCompanyInIslamicProhibitedActivity`}
                          label="Please select"
                          component={SelectAutocomplete}
                          datalistId="flagWithNa"
                          tabIndex="0"
                          isSearchable
                        />
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].roAdditionalComments`}
                          path={`${roProspectPath}.visit[${visitIndex}].roAdditionalComments`}
                          label={RO_ADDITIONAL_COMMENTS}
                          placeholder={RO_ADDITIONAL_COMMENTS}
                          multiline
                          rows="4"
                          component={Input}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].rmAdditionalComments`}
                          path={`${roProspectPath}.visit[${visitIndex}].rmAdditionalComments`}
                          label={RM_ADDITIONAL_COMMENTS}
                          placeholder={RM_ADDITIONAL_COMMENTS}
                          multiline
                          rows="4"
                          component={Input}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].soAdditionalComments`}
                          path={`${roProspectPath}.visit[${visitIndex}].soAdditionalComments`}
                          label={SO_ADDITIONAL_COMMENTS}
                          placeholder={SO_ADDITIONAL_COMMENTS}
                          multiline
                          rows="4"
                          component={Input}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>
                      <Grid item sm={12} xs={12}>
                        <Field
                          name={`visitValidations[${visitIndex}].hodAdditionalComments`}
                          path={`${roProspectPath}.visit[${visitIndex}].hodAdditionalComments`}
                          label={HOD_ADDITIONAL_COMMENTS}
                          placeholder={HOD_ADDITIONAL_COMMENTS}
                          multiline
                          rows="4"
                          component={Input}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Fragment>
                );
              })
            }
          />
          <Grid container item alignItems="center" sm={12} xs={12}>
            <Button
              title="Add Visit"
              onClick={() => {
                addVisitHandler(values, setFieldValue);
              }}
              data-testid="add-visit-button"
            />
          </Grid>
          <Divider.Dark />
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <Field
                name={`customerBackground`}
                path={`${roProspectPath}.customerBackground`}
                label={COMPANY_BACKGROUND}
                placeholder={COMPANY_BACKGROUND}
                multiline
                rows="4"
                component={Input}
                InputProps={{
                  inputProps: { maxLength: MAX_COMPANY_BACKGROUND_LENGTH, tabIndex: 0 }
                }}
              />
              <CustomerBackgroundInvalidReporter
                value={values.customerBackground}
                onFieldInvalidChange={onFieldInvalidChange}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <h5 className="mb-0">Premises details </h5>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                multiple
                name="premisesDetails.detailsOfAddressProof"
                path={`${roProspectPath}.premisesDetails.detailsOfAddressProof`}
                label="Details of Address proof"
                component={SelectAutocomplete}
                datalistId="addressProofSA"
                tabIndex="0"
                isSearchable
                extractValue={value => value}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="premisesDetails.premiseType"
                path={`${roProspectPath}.premisesDetails.premiseType`}
                label="Type of premises"
                component={SelectAutocomplete}
                datalistId="spaceType"
                tabIndex="0"
                isSearchable
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Field
                name="premisesDetails.premiseDesc"
                path={`${roProspectPath}.premisesDetails.premiseDesc`}
                placeholder="Description of premises"
                label="Description of premises"
                multiline
                rows="4"
                component={Input}
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid container item alignItems="center" sm={7} xs={12}>
              <p> Signboard of proposed company sighted? </p>
            </Grid>
            <Grid item sm={5} xs={12}>
              <Field
                name="premisesDetails.isSignboardSighted"
                path={`${roProspectPath}.premisesDetails.isSignboardSighted`}
                label=""
                component={InlineRadioGroup}
                datalistId="YesNoList"
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            {signboardProof && (
              <Grid item sm={12} xs={12}>
                <RoDocument uniqueId={documentUnique.SignBoardProof} />
              </Grid>
            )}

            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>Any other company signboard sighted along with the proposed company signboard?</p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="premisesDetails.otherCompanySignboardSighted"
                path={`${roProspectPath}.premisesDetails.otherCompanySignboardSighted`}
                label=""
                component={InlineRadioGroup}
                datalistId="otherCompanySighted"
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            {otherCompanySignboardProof && (
              <Grid item sm={12} xs={12}>
                <RoDocument uniqueId={documentUnique.OtherCompanySignBoardProof} />
              </Grid>
            )}

            <Grid item sm={12} xs={12}>
              <Field
                name="premisesDetails.relationshipDetails"
                path={`${roProspectPath}.premisesDetails.relationshipDetails`}
                placeholder="Name of company, and details about the relationship to the borrower"
                label="Name of company, and details about the relationship to the borrower"
                component={Input}
                multiline
                rows="4"
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Field
                name="premisesDetails.makaniNumber"
                path={`${roProspectPath}.premisesDetails.makaniNumber`}
                placeholder="Ejari Number"
                label="Ejari Number"
                component={Input}
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              {/* <InputGroup>
                <Field
                  name="premisesDetails.countryCode"
                  path={`${roProspectPath}.premisesDetails.countryCode`}
                  linkedFieldName="premisesDetails.telephoneNo"
                  linkedPath={`${roProspectPath}.premisesDetails.telephoneNo`}
                  datalistId="countryCode"
                  component={CustomSelect}
                  shrink={false}
                  inputProps={{ tabIndex: 0 }}
                />
                <LinkedField
                  name="premisesDetails.telephoneNo"
                  path={`${roProspectPath}.premisesDetails.telephoneNo`}
                  linkedFieldName="premisesDetails.countryCode"
                  linkedPath={`${roProspectPath}.premisesDetails.countryCode`}
                  label="Contact's mobile"
                  placeholder="Contact's mobile"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </InputGroup> */}
            </Grid>
            {!isComeFromROScreens && (
              <>
                <Grid item sm={12} xs={12} className="mb-25">
                  <LinkButton
                    type="button"
                    clickHandler={() => {
                      handleShowMap();
                    }}
                    title={"Show address on google maps"}
                  />
                </Grid>
                <Field
                  name="premisesDetails.latitude"
                  path={`${roProspectPath}.premisesDetails.latitude`}
                  type="hidden"
                />
                <Field
                  name="premisesDetails.longitude"
                  path={`${roProspectPath}.premisesDetails.longitude`}
                  type="hidden"
                />
                <Grid container spacing={3}>
                  {values.latitude && values.longitude && (
                    <Grid item alignItems="center" sm={12} xs={12}>
                      <PreviewContainer>
                        <MapPreview
                          selectedPosition={{ lat: values.latitude, lng: values.longitude }}
                          previewContainerStyle={MapPreviewContainer}
                        />
                      </PreviewContainer>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <Divider.Dark />
          <Grid container spacing={3}>
            <Grid container item alignItems="center" sm={12} xs={12}>
              <h5 className="mb-0"> More Details </h5>
            </Grid>
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p> Tenancy contract provided? </p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.tenancyContractProvided"
                path={`${roProspectPath}.additionalDetails.tenancyContractProvided`}
                label=""
                component={InlineRadioGroup}
                options={YES_NO_NA_OPTIONS}
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            <Grid item sm={9} xs={12}>
              <Field
                name="additionalDetails.tenantNameOnContract"
                path={`${roProspectPath}.additionalDetails.tenantNameOnContract`}
                placeholder="Name of tenant on tenancy contract"
                label="Name of tenant on tenancy contract"
                component={Input}
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>
                If tenancy contract not in name of borrower company, alternate proof viz
                landline/utility bill provided?
              </p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.tenantNotProposedCompany"
                path={`${roProspectPath}.additionalDetails.tenantNotProposedCompany`}
                label=""
                component={InlineRadioGroup}
                options={YES_NO_NA_OPTIONS}
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            {values.additionalDetails.tenantNotProposedCompany === "Y" && (
              <Grid item sm={12} xs={12}>
                <RoDocument uniqueId={documentUnique.CompanyTelephoneBill} />
              </Grid>
            )}
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>Is company sharing the premises with sister company or with third party?</p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.companySharingPremises"
                path={`${roProspectPath}.additionalDetails.companySharingPremises`}
                label="Please select"
                component={SelectAutocomplete}
                datalistId="flagWithNa"
                tabIndex="0"
                isSearchable
              />
            </Grid>
            {values.additionalDetails.companySharingPremises === "Yes" && (
              <Grid item sm={12} xs={12}>
                <Field
                  name="additionalDetails.remarksSharingPremises"
                  path={`${roProspectPath}.additionalDetails.remarksSharingPremises`}
                  placeholder="Remarks if company sharing premises with sister or 3rd party company?"
                  label="Remarks if company sharing premises with sister or 3rd party company?"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            )}
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>Stock sighted?</p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.stockSighted"
                path={`${roProspectPath}.additionalDetails.stockSighted`}
                label=""
                component={InlineRadioGroup}
                options={YES_NO_NA_OPTIONS}
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            {values.additionalDetails.stockSighted === "Y" && (
              <>
                <Grid container item alignItems="center" sm={6} xs={12}>
                  <p>What is estimated value of stock?</p>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field
                    name="additionalDetails.estimatedStockValueAED"
                    path={`${roProspectPath}.additionalDetails.estimatedStockValueAED`}
                    component={Input}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <DirhamSymbol />
                        </InputAdornment>
                      ),
                      inputProps: { tabIndex: 0 }
                    }}
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <Field
                    name="additionalDetails.stockDiffDescription"
                    path={`${roProspectPath}.additionalDetails.stockDiffDescription`}
                    placeholder="Please enter remarks if any"
                    multiline
                    rows="4"
                    component={Input}
                    InputProps={{
                      inputProps: { tabIndex: 0 }
                    }}
                  />
                </Grid>
              </>
            )}
            {values.additionalDetails.stockSighted === "N" && (
              <Grid item sm={12} xs={12}>
                <Field
                  name="additionalDetails.stockDiffDescription"
                  path={`${roProspectPath}.additionalDetails.stockDiffDescription`}
                  placeholder="Please put valid justification"
                  multiline
                  rows="4"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            )}
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p> Stock valuation confirmed by owner or representative? </p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.stockValueConfirmed"
                path={`${roProspectPath}.additionalDetails.stockValueConfirmed`}
                component={SelectAutocomplete}
                label="Please select"
                datalistId="stockValue"
                typeRadio
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Field
                name="additionalDetails.stockValueConfirmedRemarks"
                path={`${roProspectPath}.additionalDetails.stockValueConfirmedRemarks`}
                component={Input}
                placeholder="Remarks"
                multiline
                rows="4"
                contextualHelpProps={{ isDisableHoverListener: false }}
              />
            </Grid>
          </Grid>
          <Divider.Dark />
          {/* <Grid container spacing={3}>
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>Does the company outsource staff?</p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.companyOutsourceStaff"
                path={`${roProspectPath}.additionalDetails.companyOutsourceStaff`}
                label="Please select"
                component={SelectAutocomplete}
                datalistId="YesNoList"
                tabIndex="0"
              />
            </Grid>
          </Grid> */}
          <Grid container spacing={3}>
            <Grid container item alignItems="center" sm={6} xs={12}>
              <p>Does the company utilize outsourced staff?</p>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="additionalDetails.companyUtiliseOutsourceStaff"
                path={`${roProspectPath}.additionalDetails.companyUtiliseOutsourceStaff`}
                label="Please select"
                component={SelectAutocomplete}
                datalistId="flagWithNa"
                tabIndex="0"
                isSearchable
              />
            </Grid>
            {values.additionalDetails.companyUtiliseOutsourceStaff === "Yes" && (
              <>
                <Grid item sm={12} xs={12}>
                  <RoDocument uniqueId={documentUnique.ProofOfOutsourceStaff} />
                </Grid>
                {!isOutsourcedStaffProofUploaded && (
                  <div className="docError"> Please upload the document in order to proceed </div>
                )}
              </>
            )}
            <Grid item sm={9} xs={12}>
              <Field
                name="additionalDetails.approxNumberofStaff"
                path={`${roProspectPath}.additionalDetails.approxNumberofStaff`}
                label="Approximate number of staff or labourers seen during visit?"
                placeholder="Approximate number of staff or labourers seen during visit?"
                component={Input}
                InputProps={{
                  inputProps: { tabIndex: 0 }
                }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" mb={2}>
            <Grid item sm={12} xs={12} display="flex" justifyContent="flex-end">
              <SubmitButton
                withRightArrow={false}
                disabled={
                  values.additionalDetails.companyUtiliseOutsourceStaff === "Yes" &&
                  !isOutsourcedStaffProofUploaded
                }
                label="Continue"
                type="submit"
              />
            </Grid>
          </Grid>
        </FormStyle>
      ))}
    </Formik>
  );
};

CompanyDetails.propTypes = {
  handleContinue: PropTypes.func.isRequired,
  createFormChangeHandler: PropTypes.func.isRequired,
  otherCompanySignboardProof: PropTypes.bool,
  outsourcedStaffProof: PropTypes.bool,
  telephoneOrDEWAProof: PropTypes.bool,
  organizationInfoRO: PropTypes.object.isRequired,
  signboardProof: PropTypes.bool,
  updateVisit: PropTypes.func.isRequired,
  isExistingCustomer: PropTypes.bool,
  onFieldInvalidChange: PropTypes.func,
  invalidFields: PropTypes.object
};
